import * as WeightUtils from "~/utils/WeightUtils";
import { type LineItem } from "~/features/cart/cart/models/CartData";
import { transformLinear, transformProduct } from "~/features/productDetails/transformers/ProductDataTransformer";
import { toMapFn } from "~/common/utils/reducers";
import { type NetPriceData } from "~/domains/pricing/types/PriceData";
import { isSubscription } from "~/utils/productUtils";
import { isSurchargeIncludedInPriceValue } from "~/utils/features";
import type { ProductData } from "~/domains/product/types/ProductData";
import { isQuotePackEntryValid } from "~/features/cart/cart/redux/reducers/lineItemValidations";
import type { PriceDataDTO } from "~/domains/pricing/types/PriceDataDTO";
import type { ProductDataDTO, ImageDataDTO } from "~/domains/product/types/ProductDataDTO";
import type { PackageEntrySource } from "~/features/cart/cart/models/CartData";
import type { TransformedCartData } from "~/features/cart/cart/transformers/CartResponseTransformer";

// Local structural types — kept here so this file does not depend on the
// deleted legacy cart DTO or transformer files.

interface ProductLinearInfoDTO {
  designation: string;
  sapMaterialNumber: string;
  aliasesString: string;
  images: ImageDataDTO[];
  leadTime: number;
  guideways: string;
  carriages: string;
  configId: string;
}

interface LineItemDTO {
  pk: number;
  entryNumber: number;
  quantity: number;
  packageSize?: number;
  packageCode: string;
  comment?: string;
  selectedDeliveryDate?: number;
  netPrice: PriceDataDTO;
  priceUnit: number | null;
  primaryWarehouseName: string | null;
  primaryWarehouseCode: string | null;
  priceReference: string | null;
  internalComment: string | null;
  minimumSellingPrice: PriceDataDTO | null;
  recommendedSellingPrice: PriceDataDTO | null;
  offerPrice: PriceDataDTO | null;
  targetPrice: PriceDataDTO | null;
  source: PackageEntrySource;
  surcharge: number | null;
  originCountry?: string;
}

interface CartViewEntryDTO {
  pk: number;
  entryNumber: number;
  product: ProductDataDTO;
  packageEntries: LineItemDTO[];
  productLinearInfo?: ProductLinearInfoDTO;
}

export interface QuoteResponseDTO {
  code: string;
  entries: CartViewEntryDTO[] | null;
  dstError: boolean;
  quoteAccount: {
    uid: string;
    displayUid: string;
  } | null;
}

export function transform(dto: QuoteResponseDTO): TransformedCartData {
  const entries = dto.entries || [];

  const accountNumber = (dto.quoteAccount && dto.quoteAccount.displayUid) || "";

  const allPackageEntries: LineItem[] = [];
  const productCodes: string[] = [];
  const products: ProductData[] = [];

  entries.forEach((entry) => {
    const product = entry.productLinearInfo
      ? transformLinear(entry.product, entry.productLinearInfo)
      : transformProduct(entry.product);
    productCodes.push(product.code);
    products.push(product);
    const packageEntries = (entry.packageEntries || []).map((dto) => transformPackageEntry(dto, product, entry.pk));
    allPackageEntries.push(...packageEntries);
  });

  const packageEntriesById = toMapFn(allPackageEntries, (entry) => entry.id);
  const packageEntries = allPackageEntries.map((e) => e.id);

  return {
    accountNumber,
    packageEntries,
    packageEntriesById,
    entryGroups: [],
    entryGroupsByNumber: {},
    productCodes,
    products,
    dstError: dto.dstError,
    comment: "",
  };
}

function transformPackageEntry(dto: LineItemDTO, product: ProductData, orderEntryPk: number): LineItem {
  const deliveryDate = dto.selectedDeliveryDate ? new Date(dto.selectedDeliveryDate) : new Date();

  const weight = product.weight ?? WeightUtils.zero();

  const offerPrice = dto.offerPrice || undefined;
  const targetPrice = dto.targetPrice || undefined;

  let netPrice: NetPriceData | undefined = undefined;
  if (dto.netPrice) {
    const surcharge = dto.surcharge ?? 0;
    const value = dto.netPrice.value;
    const baseValue = isSurchargeIncludedInPriceValue() ? value : value / (1 + surcharge);
    netPrice = {
      value,
      baseValue,
      surcharge,
      currencyIso: dto.netPrice.currencyIso,
      unit: dto.priceUnit ?? 1,
      isSubscription: isSubscription(product),
    };
  }

  const item: LineItem = {
    pk: dto.pk,
    id: dto.pk,
    cartEntryPk: orderEntryPk,
    productCode: product.code,
    groupNumbers: [],
    comment: dto.comment || "",
    deliveryDate: deliveryDate,
    manuallySetRDD: false,
    packSize: dto.packageSize,
    packCode: dto.packageCode,
    quantity: dto.quantity,
    weight: weight,
    netPrice,
    valid: true,
    entryNumber: dto.entryNumber,
    primaryWarehouseName: dto.primaryWarehouseName || "",
    primaryWarehouseCode: dto.primaryWarehouseCode || "",
    internalComment: dto.internalComment || "",
    showComments: false,
    priceOverriden: !!offerPrice,
    offerPrice,
    msp: dto.minimumSellingPrice || undefined,
    rsp: dto.recommendedSellingPrice || undefined,
    targetPrice,
    messages: [],
    contractNumber: "",
    contractLine: "",
    disableEdit: false,
    source: dto.source,
    priceReference: dto.priceReference ?? "",

    edd: null,
    isInStock: null,

    // Quote fields
    quoteLine: "",
    quoteNumber: "",
    minimumQty: 1,
    isBundled: false,
    original: true,
    quoteExpiryDate: null,
    estimatedShippingDate: null,

    // UI Fields
    isPackagingSelectionOpen: false,

    // Salesmen are never restricted
    restrictedPackage: false,

    customerOrderNumber: "",
    countryOfOrigin: dto.originCountry ?? "",
    tariffSurcharge: false,
  };

  item.valid = isQuotePackEntryValid(item);
  return item;
}
